import React, { useEffect, useMemo, useRef, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { WithoutKitSchedule } from "../../../../services/v2/sales.service";
import { FilterOptions, apiError } from "../kits/shared";
import { Multi } from "../../managment/enrollmentRequests/filtrosMatriculas";
import { Chip, Dato, Sub, Ventana } from "./piezasTienda";

/*
	Ventas › Sin Kit (Módulos Claros, 2026-11-01): junta Inscritos Recientes, Alumnos sin Producto y Maestros › Horarios sin Kit.
	Un renglón por horario activo (o por empezar) con su maestra y su % con kit; el clic abre la ventana con las alumnas que no
	tienen kit. «% con kit» = KitService::withKitSql en el backend (una sola definición para todas las pestañas).
*/

const DIAS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const horaAmPm = (cincos: number | null): string => {
	if (cincos === null || cincos === undefined) return "";
	const minutos = Number(cincos) * 6; // la app guarda la hora en bloques de 6 minutos (70 = 7:00 am, 150 = 3:00 pm)
	const h = Math.floor(minutos / 60), m = minutos % 60;
	return (h % 12 === 0 ? 12 : h % 12) + ":" + String(m).padStart(2, "0") + (h < 12 ? " am" : " pm");
};
export const horarioCorto = (s: WithoutKitSchedule): string => [s.weekDays.map((d) => DIAS[d] || "").filter(Boolean).join(", "), horaAmPm(s.startHours)].filter(Boolean).join(" ");

export interface TabSinKitProps { campusId: number; movil: boolean; q: string; opciones: FilterOptions | null; onCuenta: (n: number) => void }

const TabSinKit: React.FC<TabSinKitProps> = ({ campusId, movil, q, opciones, onCuenta }) => {
	const http = HttpManager.getInstance().salesService;
	const [cursos, setCursos] = useState<string[]>([]);
	const [maestras, setMaestras] = useState<string[]>([]);
	const [periodos, setPeriodos] = useState<string[]>([]);
	const [soloBajos, setSoloBajos] = useState<boolean>(false);
	const [rows, setRows] = useState<WithoutKitSchedule[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [abierto, setAbierto] = useState<WithoutKitSchedule | null>(null);
	const requestRef = useRef<number>(0);

	useEffect(() => {
		if (!campusId) return;
		const request = ++requestRef.current;
		setError(null);
		http.withoutKit(campusId, { courseIds: cursos, mentorIds: maestras, periodIds: periodos, q, onlyMissing: 1, onlyLow: soloBajos ? 1 : undefined })
			.then((r) => { if (request === requestRef.current) { setRows(r.data); if (!cursos.length && !maestras.length && !periodos.length && !q && !soloBajos) onCuenta(r.data.length); } })
			.catch((e) => { if (request === requestRef.current) { setError(apiError(e, "No se pudo cargar.")); setRows([]); } });
	}, [campusId, cursos.join(","), maestras.join(","), periodos.join(","), q, soloBajos]);

	const sinKit = useMemo(() => (rows || []).reduce((n, r) => n + r.withoutKit, 0), [rows]);
	const pct = (v: number | null) => v === null ? "-" : v.toLocaleString("es-MX", { maximumFractionDigits: 1 }) + " %";
	const hay = cursos.length || maestras.length || periodos.length || soloBajos;

	const lista = () => {
		if (!rows) return <div className="loader" />;
		if (rows.length < 1) return <div className="wvtSub" style={{ textAlign: "center", padding: 24, fontSize: 15 }}>Todos los horarios tienen su kit.</div>;
		if (movil) return <div style={{ display: "flex", flexDirection: "column", gap: 8, paddingBottom: 8 }}>
			{rows.map((r) => <div key={r.scheduleId} className="wvtTarjeta" role="button" tabIndex={0} onClick={() => setAbierto(r)}>
				<div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
					<div><b>{r.course}</b><Sub>{horarioCorto(r)}{r.period ? ", " + r.period : ""}</Sub></div>
					<div style={{ flexShrink: 0 }}><Chip tono={r.low ? "oro" : undefined}>{pct(r.kitRate)}</Chip></div>
				</div>
				<Sub>{r.mentorShort || "Sin Maestra"} · {r.withoutKit} sin kit de {r.students}</Sub>
			</div>)}
		</div>;
		return <table className="table table-hover wvtLista">
			<thead><tr><th>Horario</th><th style={{ width: 200 }}>Maestra</th><th className="wvtNum" style={{ width: 90 }}>Alumnas</th><th className="wvtNum" style={{ width: 90 }}>Sin Kit</th><th className="wvtNum" style={{ width: 110 }}>% con Kit</th></tr></thead>
			<tbody>{rows.map((r) => <tr key={r.scheduleId} className="wvtFila" onClick={() => setAbierto(r)}>
				<td><b>{r.course}</b>{r.module && r.module !== r.course ? <span className="wvtSub" style={{ display: "inline" }}> {r.module}</span> : null}<Sub>{horarioCorto(r)}{r.period ? ", " + r.period : ""}</Sub></td>
				<td>{r.mentorShort || <span className="wvtSub">Sin Maestra</span>}</td>
				<td className="wvtNum">{r.students}</td>
				<td className="wvtNum"><span className="wvtOro">{r.withoutKit}</span></td>
				<td className="wvtNum"><Chip tono={r.low ? "oro" : undefined}>{pct(r.kitRate)}</Chip></td>
			</tr>)}</tbody>
		</table>;
	};

	return <>
		<div className="waFmBarra">
			<div className="waFmBarraFecha">
				<div className="waFmSegs">
					<button type="button" className={"waFmSeg" + (!soloBajos ? " activo" : "")} onClick={() => setSoloBajos(false)}>Todos</button>
					<button type="button" className={"waFmSeg" + (soloBajos ? " activo" : "")} onClick={() => setSoloBajos(true)}>Menos de la Mitad</button>
				</div>
			</div>
			<div className="waFmBarraListas">
				<Multi titulo="Curso" buscar opciones={(opciones?.courses || []).map((o) => ({ id: o.id, name: o.name }))} elegidos={cursos} onChange={setCursos} />
				<Multi titulo="Maestra" buscar opciones={(opciones?.mentors || []).map((o) => ({ id: String(o.id), name: o.name }))} elegidos={maestras} onChange={setMaestras} />
				<Multi titulo="Periodo" opciones={(opciones?.periods || []).map((o) => ({ id: String(o.id), name: o.name }))} elegidos={periodos} onChange={setPeriodos} />
				{hay ? <button type="button" className="btn btn-link btn-sm waFmBarraLimpiar" onClick={() => { setCursos([]); setMaestras([]); setPeriodos([]); setSoloBajos(false); }}>Limpiar Filtros</button> : null}
			</div>
		</div>
		{rows ? <div className="wvtKpis">
			<div><small>Horarios</small><b>{rows.length}</b></div>
			<div><small>Alumnas Sin Kit</small><b>{sinKit}</b></div>
		</div> : null}
		{error ? <div className="alert alert-danger" style={{ marginTop: 12 }}>{error}</div> : null}
		<div className="waScrollX" style={{ overflowX: "auto", marginTop: 14, paddingBottom: 8 }}>{lista()}</div>
		{abierto ? <AlumnasSinKit campusId={campusId} horario={abierto} onClose={() => setAbierto(null)} /> : null}
	</>;
};

const AlumnasSinKit: React.FC<{ campusId: number; horario: WithoutKitSchedule; onClose: () => void }> = ({ campusId, horario, onClose }) => {
	const [alumnas, setAlumnas] = useState<any[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	useEffect(() => {
		HttpManager.getInstance().salesService.withoutKitStudents(campusId, horario.scheduleId)
			.then((r) => setAlumnas(r.data)).catch((e) => { setError(apiError(e, "No se pudo cargar.")); setAlumnas([]); });
	}, [campusId, horario.scheduleId]);
	const pct = horario.kitRate === null ? "-" : horario.kitRate.toLocaleString("es-MX", { maximumFractionDigits: 1 }) + " %";
	return <Ventana onClose={onClose} titulo={<><span>{horario.course}{horario.module && horario.module !== horario.course ? " " + horario.module : ""}</span><Chip tono={horario.low ? "oro" : undefined}>{pct} con Kit</Chip></>}>
		<div className="wvtDatos">
			<Dato t="Horario">{horarioCorto(horario)}<Sub>{horario.code}</Sub></Dato>
			<Dato t="Periodo">{horario.period || "-"}</Dato>
			<Dato t="Maestra">{horario.mentorShort || "Sin Maestra"}</Dato>
			<Dato t="Alumnas">{horario.students}<Sub>{horario.withKit} con kit</Sub></Dato>
		</div>
		<div className="wvtSeccion">
			<h6>Sin Kit</h6>
			{error ? <div className="alert alert-danger">{error}</div> : !alumnas ? <div className="loader" /> : alumnas.length < 1 ? <div className="wvtSub">-</div> :
				<table className="table table-sm wvtTabla"><tbody>
					{alumnas.map((a) => <tr key={a.studentId}><td><b>{a.name}</b><Sub>{[a.identityCode, a.phone].filter(Boolean).join(", ")}</Sub></td><td className="wvtNum wvtSub">{a.enrolledAt ? "Inscrita " + a.enrolledAt : ""}</td></tr>)}
				</tbody></table>}
		</div>
	</Ventana>;
};

export default TabSinKit;
